import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import CachedIcon from '@material-ui/icons/Cached';
import Select, { components } from "react-select";
import SearchIcon from "mdi-react/SearchIcon";
import { setAuthProgram, getAuthUser, getOrganization } from "@/containers/App/auth";
import { getProgram } from "@/services/program/getProgram";
import { getProgramBalance } from "@/services/program/getBalance";
import { getProgramTree, getManagerSwitcherPrograms } from "@/services/program/getProgramTree";
import { fetchUserPrograms } from "@/shared/apiHelper";
import { cacheProgramTree, getCachedProgramTree } from "@/shared/helpers";
import { useTranslation } from "react-i18next";

const MANAGER_ROLE_NAMES = ["manager", "limited manager", "read only manager", "read-only manager"];

const hasManagerRole = (programEntry) => {
  if (!programEntry) return false;
  const rawRoles = programEntry.roles ?? programEntry.role;
  if (!rawRoles) return true;
  if (typeof rawRoles === "string") {
    return rawRoles.toLowerCase().includes("manager");
  }
  if (Array.isArray(rawRoles)) {
    if (rawRoles.length === 0) return true;
    return rawRoles.some((role) => {
      const roleName = (typeof role === "string" ? role : role?.name || "").toLowerCase();
      return roleName.includes("manager");
    });
  }
  if (typeof rawRoles === "object") {
    if (rawRoles.name && typeof rawRoles.name === "string") {
      return rawRoles.name.toLowerCase().includes("manager");
    }
    const roleValues = Object.values(rawRoles);
    if (roleValues.length === 0) return true;
    return roleValues.some((role) => {
      const roleName = (typeof role === "string" ? role : role?.name || "").toLowerCase();
      return roleName.includes("manager");
    });
  }
  return true;
};

const normalizeDomainName = (d) => {
  if (!d) return "";
  let name = typeof d === "string" ? d : d.name || d.domain || "";
  name = name.replace(/^https?:\/\//i, "").split(":")[0].split("/")[0].toLowerCase().trim();
  return name;
};

const getCurrentDomainName = (domain) => {
  const fromDomainObj = normalizeDomainName(domain?.name || domain?.domain?.name || domain?.domain_name);
  if (fromDomainObj) return fromDomainObj;
  if (typeof window !== "undefined" && window.location?.hostname) {
    const host = normalizeDomainName(window.location.hostname);
    if (host && host !== "localhost" && host !== "127.0.0.1") {
      return host;
    }
  }
  return "";
};

const getCurrentDomainId = (domain) => {
  return domain?.id || domain?.domain?.id || null;
};

const matchesCurrentDomain = (programEntry, currentDomainName, currentDomainId) => {
  if (!programEntry) return false;

  // Check domain_id if available
  if (currentDomainId && programEntry.domain_id && String(programEntry.domain_id) === String(currentDomainId)) {
    return true;
  }

  // Check programEntry.domain
  if (programEntry.domain) {
    const pDomainName = normalizeDomainName(programEntry.domain);
    if (pDomainName && currentDomainName && pDomainName === currentDomainName) {
      return true;
    }
    if (currentDomainId && typeof programEntry.domain === "object" && programEntry.domain?.id) {
      if (String(programEntry.domain.id) === String(currentDomainId)) {
        return true;
      }
    }
  }

  // Check programEntry.domains (array)
  if (Array.isArray(programEntry.domains) && programEntry.domains.length > 0) {
    const hasMatch = programEntry.domains.some((d) => {
      const dName = normalizeDomainName(d);
      if (dName && currentDomainName && dName === currentDomainName) return true;
      if (currentDomainId && typeof d === "object" && d?.id && String(d.id) === String(currentDomainId)) return true;
      return false;
    });
    if (hasMatch) return true;
  }

  // Check programEntry.default_domain
  if (programEntry.default_domain) {
    const defDomains = Array.isArray(programEntry.default_domain)
      ? programEntry.default_domain
      : [programEntry.default_domain];
    const hasMatch = defDomains.some((d) => {
      const dName = normalizeDomainName(d);
      if (dName && currentDomainName && dName === currentDomainName) return true;
      if (currentDomainId && typeof d === "object" && d?.id && String(d.id) === String(currentDomainId)) return true;
      return false;
    });
    if (hasMatch) return true;
  }

  // Check programEntry.domain_name
  if (programEntry.domain_name) {
    const pDomainName = normalizeDomainName(programEntry.domain_name);
    if (pDomainName && currentDomainName && pDomainName === currentDomainName) {
      return true;
    }
  }

  return false;
};

const findNodeInTree = (nodes, targetId) => {
  if (!Array.isArray(nodes)) return null;
  for (const node of nodes) {
    if (Number(node.id) === Number(targetId)) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeInTree(node.children, targetId);
      if (found) return found;
    }
  }
  return null;
};

const dedupeByValue = (options) => {
  const seen = new Set();
  return options.filter((option) => {
    const key = String(option.value || option.id);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const SelectProgram = ({
  auth,
  program,
  rootProgram,
  domain,
  showRefresh,
  onChange,
  selected = null,
  label = 'Program',
  tooltip = 'Switch to a different program to manage',
  variant = 'default',
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const switcherLoadedRef = useRef(false);
  const isManagerReportVariant = variant === "manager-report";
  const controlHeight = isManagerReportVariant ? "1.9rem" : "2rem";
  const controlRadius = isManagerReportVariant ? "12px" : "10px";
  const controlFontSize = isManagerReportVariant ? "0.8rem" : "0.8125rem";
  const optionFontSize = isManagerReportVariant ? "0.8rem" : "0.875rem";
  const optionChildFontSize = isManagerReportVariant ? "0.76rem" : "0.8125rem";

   const isSwitcherMode = typeof onChange !== "function";

   useEffect(() => {
     switcherLoadedRef.current = false;
   }, [auth?.id]);

   useEffect(() => {
     if (isSwitcherMode) {
       if (switcherLoadedRef.current) return;
       switcherLoadedRef.current = true;

       const loadSwitcherOptions = async () => {
         const currentDomain =
           domain ||
           (typeof localStorage !== "undefined"
             ? JSON.parse(localStorage.getItem("icoDomain") || "null")
             : null);
         const currentDomainName = getCurrentDomainName(currentDomain);
         const currentDomainId = getCurrentDomainId(currentDomain);
         const domainRootProgramId =
           currentDomain?.program?.id ||
           currentDomain?.program_id ||
           rootProgram?.id ||
           program?.id;

         const orgId =
           currentDomain?.program?.organization_id ||
           rootProgram?.organization_id ||
           rootProgram?.organization?.id ||
           program?.organization_id ||
           program?.organization?.id ||
           auth?.organization_id ||
           auth?.organization?.id ||
           (typeof getOrganization === "function" ? getOrganization()?.id : null);
         const userId = auth?.id || (typeof getAuthUser === "function" ? getAuthUser()?.id : null);

         if (orgId && domainRootProgramId && userId) {
           try {
             const backendOptions = await getManagerSwitcherPrograms(orgId, domainRootProgramId, userId);
             if (Array.isArray(backendOptions) && backendOptions.length > 0) {
               const switcherOptions = dedupeByValue(backendOptions);
               setOptions(switcherOptions);
               setDefaultSelected(switcherOptions);
               return;
             }
           } catch (e) {
             // fallback to client-side resolution
           }
         }

         let hierarchyTree = null;
         if (orgId && domainRootProgramId) {
           try {
             hierarchyTree = await getProgramTree(orgId, domainRootProgramId);
           } catch (e) {
             // tree fetch fallback
           }
         }

         let seeds = buildSwitcherSeeds();

         if (userId && orgId) {
           try {
             const rawUserPrograms = await fetchUserPrograms(orgId, userId);
             const fetchedPrograms = Array.isArray(rawUserPrograms)
               ? rawUserPrograms
               : (rawUserPrograms?.data || rawUserPrograms?.programs || (typeof rawUserPrograms === 'object' && rawUserPrograms !== null ? Object.values(rawUserPrograms) : []));

             if (fetchedPrograms.length > 0) {
               const managerFetched = fetchedPrograms.filter(hasManagerRole);
               if (managerFetched.length > 0) {
                 seeds = dedupeByValue([...seeds, ...managerFetched]);
               }
             }
           } catch (e) {
             // fallback to existing seeds
           }
         }

         let optionsList = [];

         if (hierarchyTree && Array.isArray(hierarchyTree) && hierarchyTree.length > 0) {
           const assignedNodes = [];
           seeds.forEach((seed) => {
             const seedId = Number(seed.id || seed.value);
             const node = findNodeInTree(hierarchyTree, seedId);
             if (node) {
               assignedNodes.push(node);
             } else if (matchesCurrentDomain(seed, currentDomainName, currentDomainId)) {
               assignedNodes.push({ id: seedId, name: seed.name || seed.title, children: [] });
             }
           });

           if (assignedNodes.length > 0) {
             assignedNodes.forEach((node) => {
               optionsList = optionsList.concat(flattenPrograms([node]));
             });
           } else {
             optionsList = flattenPrograms(hierarchyTree);
           }
         } else {
           const seedOptionLists = await Promise.all(
             seeds.map((seed) => {
               const sOrgId = seed.organization_id || seed.organization?.id || orgId;
               return getProgramTree(sOrgId, seed.id)
                 .then((tree) => {
                   const flattened = flattenPrograms(tree);
                   return flattened.length
                     ? flattened
                     : [{ value: seed.id, label: seed.name || seed.title || `Program #${seed.id}`, depth: 0 }];
                 })
                 .catch(() => [{ value: seed.id, label: seed.name || seed.title || `Program #${seed.id}`, depth: 0 }]);
             })
           );
           optionsList = seedOptionLists.flat();
         }

         const switcherOptions = dedupeByValue(optionsList);
         setOptions(switcherOptions);
         setDefaultSelected(switcherOptions);
       };
       loadSwitcherOptions();
       return;
     }

     const cachedTree = getCachedProgramTree();
     if (cachedTree) {
       const flatOptions = flattenPrograms(cachedTree);
       setOptions(flatOptions);
       setDefaultSelected(flatOptions);
     } else {
       const fallbackOrgId =
         rootProgram?.organization_id ||
         rootProgram?.organization?.id ||
         program?.organization_id ||
         program?.organization?.id ||
         auth?.organization_id ||
         auth?.organization?.id ||
         (typeof getOrganization === 'function' ? getOrganization()?.id : null);
       if (rootProgram && rootProgram.id && fallbackOrgId) {
         getProgramTree(fallbackOrgId, rootProgram.id).then(
           (tree) => {
             cacheProgramTree(tree);
             const flatOptions = flattenPrograms(tree);
             setOptions(flatOptions);
             setDefaultSelected(flatOptions);
           }
         );
       }
     }
   }, [rootProgram?.id, program?.id, auth?.id, domain?.id]);

   const flattenPrograms = (programs, depth = 0) => {
     let list = [];
     if (!Array.isArray(programs)) return list;
     programs.forEach((p) => {
       list.push({
         value: p.id,
         label: p.name || p.title || `Program #${p.id}`,
         depth: depth,
         isDisabled: p.type == 'shell'
       });
       if (p.children && p.children.length) {
         list = list.concat(flattenPrograms(p.children, depth + 1));
       }
     });
     return list;
   };

   const buildSwitcherSeeds = () => {
     if (rootProgram?.id) {
       return [rootProgram];
     }
     let rawRoles = auth?.programRoles;
     if (!rawRoles && typeof getAuthUser === 'function') {
       const user = getAuthUser();
       rawRoles = user?.programRoles;
     }
     const roleEntries = Array.isArray(rawRoles)
       ? rawRoles
       : Object.values(rawRoles ?? {});
     const managerPrograms = roleEntries.filter(hasManagerRole);
     if (managerPrograms.length) {
       return managerPrograms;
     }
     const seed = program;
     return seed ? [seed] : [];
   };

  const setDefaultSelected = (optionsList) => {
    const selectedValue = selected || program?.id;
    const found = optionsList.find((opt) => opt.value === selectedValue);
    if (found) setSelectedOption(found);
  };

   const handleChange = (selected) => {
     setSelectedOption(selected);
     if (typeof onChange === "function") {
       onChange({ target: { value: selected.value } }); // mimic event
       return;
     }
     const targetOrgId =
       rootProgram?.organization_id ||
       rootProgram?.organization?.id ||
       program?.organization_id ||
       program?.organization?.id ||
       auth?.organization_id ||
       auth?.organization?.id ||
       (typeof getOrganization === 'function' ? getOrganization()?.id : null);

     getProgram(targetOrgId, selected.value).then((p) => {
       getProgramBalance(targetOrgId, p.id).then((balance) => {
         p.balance = balance;
         setAuthProgram(p);
         window.location.reload();
       });
     });
   };

  const refreshProgramTree = () => {
    const targetOrgId =
      rootProgram?.organization_id ||
      rootProgram?.organization?.id ||
      program?.organization_id ||
      program?.organization?.id ||
      auth?.organization_id ||
      auth?.organization?.id ||
      (typeof getOrganization === 'function' ? getOrganization()?.id : null);
    const targetProgId = rootProgram?.id || program?.id;
    if (targetOrgId && targetProgId) {
      getProgramTree(targetOrgId, targetProgId).then((tree) => {
        cacheProgramTree(tree);
        const flatOptions = flattenPrograms(tree);
        setOptions(flatOptions);
        setDefaultSelected(flatOptions);
      });
    }
  };

  if (!auth || !program) return t("loading");

  return (
    <>
      <div
        className="form__form-group-field d-flex"
        data-tip={label ? tooltip : undefined}
        style={{ alignItems: "center", width: "100%" }}
      >
        {label && (
          <span
            className="form__form-group-label"
            style={{
              fontSize: "0.8125rem",
              marginRight: "0.5rem",
              whiteSpace: "nowrap",
              flex: "0 0 auto",
            }}
          >
            {label}:
          </span>
        )}
         <div style={{ flex: "1 1 0", minWidth: 0 }}>
          <Select
            value={selectedOption}
            onChange={handleChange}
            options={options}
            isSearchable
            placeholder={t("Search programs...")}
            className="react-select"
            classNamePrefix="react-select"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            formatOptionLabel={(option, { context, selectValue }) => {
              if (context === "value") return option.label;
              const depth = option.depth || 0;
              const isSelected = selectValue.some(v => v.value === option.value);
              return (
                <div style={{
                  paddingLeft: `${depth * 20}px`,
                  color: isSelected ? "#ffffff" : depth > 0 ? "#6b7280" : "#111827",
                  fontSize: depth > 0 ? optionChildFontSize : optionFontSize,
                }}>
                  {option.label}
                </div>
              );
            }}
            components={{
              DropdownIndicator: (props) => (
                <components.DropdownIndicator {...props}>
                  <SearchIcon size={18} />
                </components.DropdownIndicator>
              ),
            }}
            styles={{
              container: (base) => ({
                ...base,
                flex: "1 1 auto",
                minWidth: "0",
                maxWidth: "100%",
                width: "100%",
              }),
              control: (base) => ({
                ...base,
                borderRadius: controlRadius,
                cursor: "text",
                minHeight: controlHeight,
                height: controlHeight,
                fontSize: controlFontSize,
                borderColor: isManagerReportVariant ? "rgba(148, 163, 184, 0.22)" : base.borderColor,
                boxShadow: "none",
              }),
              valueContainer: (base) => ({
                ...base,
                height: controlHeight,
                padding: "0 0.5rem",
              }),
              input: (base) => ({
                ...base,
                margin: 0,
                padding: 0,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: controlHeight,
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 2200,
                ...(isManagerReportVariant
                  ? {}
                  : {
                      minWidth: "18rem",
                      maxWidth: "min(28rem, calc(100vw - 32px))",
                    }),
              }),
              menu: (base) => ({
                ...base,
                textAlign: "left",
                left: 0,
                right: "auto",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
                zIndex: 2200,
                ...(isManagerReportVariant
                  ? {}
                  : {
                      minWidth: "100%",
                    }),
              }),
              option: (base, state) => ({
                ...base,
                textAlign: "left",
                padding: "0.5rem 0.75rem",
                fontWeight: state.isSelected ? "bold" : "normal",
                backgroundColor: state.isSelected
                  ? "#F97316"
                  : state.isFocused
                  ? "rgba(249, 115, 22, 0.08)"
                  : "white",
                color: state.isSelected ? "#ffffff" : "#333",
                fontSize: controlFontSize,
              }),
              indicatorSeparator: () => ({
                display: "none",
              }),
              singleValue: (base) => ({
                ...base,
                textAlign: "left",
                fontSize: controlFontSize,
              }),
            }}
            theme={(theme) => ({
              ...theme,
              borderRadius: 12,
              colors: {
                ...theme.colors,
                primary: "#F97316",
                primary25: "rgba(249, 115, 22, 0.08)",
                primary50: "rgba(249, 115, 22, 0.14)",
              },
            })}
          />
        </div>
      </div>
      {showRefresh && <span alt="Refresh Program Tree"><CachedIcon style={{ cursor: "pointer" }} onClick={refreshProgramTree} /></span>}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    program: state.program,
    rootProgram: state.rootProgram,
    domain: state.domain,
  };
};

export default connect(mapStateToProps)(SelectProgram);
